import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Cog, MessageSquare, Trophy } from "lucide-react";

/** date-fns' `format` throws on Invalid Date — which is what an
 *  undefined / malformed `occurred_at` lands on after `new Date(…)`.
 *  Wrap once and use everywhere in this file so a partial deal
 *  payload doesn't blow up the whole section. */
function safeFormat(value: unknown, fmt = "MMM d"): string {
  if (value == null || value === "") return "—";
  try {
    const d = value instanceof Date ? value : new Date(value as string);
    if (Number.isNaN(d.getTime())) return "—";
    return format(d, fmt);
  } catch {
    return "—";
  }
}
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { resolveAvatarColor } from "@/lib/avatarColor";
import { paletteFor } from "@/components/tasks/statusPalette";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/formatters";
import type {
  DealAttributionEntry,
  DealJourneyStep,
  DealContributor,
} from "@/api/analytics";

interface DealAttributionProps {
  deals?: DealAttributionEntry[];
  projectSlug: string;
  loading?: boolean;
}

/**
 * Section 8 — Deal journey visualisation. One card per won deal, each
 * showing a horizontal timeline of pipeline-stage milestones plus a
 * contributor list with role badges. Cards link back to the company
 * detail page.
 *
 * The timeline is a flex row so it grows / shrinks with the available
 * width; dots and connectors are positioned with `flex-1` between
 * milestones rather than absolute positioning, which scales cleanly
 * to any number of steps.
 */
export function DealAttribution({
  deals,
  projectSlug,
  loading,
}: DealAttributionProps) {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div
            key={i}
            className="h-[200px] animate-pulse rounded-plane bg-white shadow-plane"
          />
        ))}
      </div>
    );
  }
  if (!deals || deals.length === 0) {
    return (
      <section className="rounded-plane bg-white p-8 text-center shadow-plane">
        <span className="text-3xl" aria-hidden>
          🎯
        </span>
        <p className="mt-2 text-sm font-medium text-slate-700">
          No deals won in this period.
        </p>
        <p className="text-xs text-slate-400">Keep pushing!</p>
      </section>
    );
  }
  return (
    <div className="space-y-3">
      {deals.map((deal, idx) => (
        <DealCard
          key={deal.company_id ?? idx}
          deal={deal}
          projectSlug={projectSlug}
        />
      ))}
    </div>
  );
}

function DealCard({
  deal,
  projectSlug,
}: {
  deal: DealAttributionEntry;
  projectSlug: string;
}) {
  // Sub-arrays land here defaulted so any per-deal access below is
  // safe: a backend that omits `steps` or `contributors` for a deal
  // (or sends them as null) used to crash the section.
  const steps = deal.steps ?? [];
  const contributors = deal.contributors ?? [];
  return (
    <Link
      to={`/projects/${projectSlug}/companies/${deal.company_id}`}
      className="block rounded-plane bg-white p-5 shadow-plane transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-plane-lift motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <Trophy className="h-4 w-4 shrink-0 text-amber-500" />
          <h3 className="truncate text-base font-semibold text-slate-900">
            {deal.company_name}
          </h3>
          {deal.sector && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
              {deal.sector}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500">
          Won {safeFormat(deal.won_at)}
          {deal.cycle_days > 0 && (
            <>
              {" · "}
              {deal.cycle_days}{" "}
              {deal.cycle_days === 1 ? "day" : "days"}
            </>
          )}
        </div>
      </header>

      <Timeline steps={steps} />

      {contributors.length > 0 && (
        <div className="mt-4 space-y-1.5">
          <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
            Contributors
          </div>
          {contributors.map((c, i) => (
            <ContributorRow
              // member_id is undefined for "Engine" / system rows;
              // appending the index keeps the key unique even when
              // multiple system contributors share the same role.
              key={`${c.member_id ?? "system"}-${c.role ?? "role"}-${i}`}
              contributor={c}
            />
          ))}
        </div>
      )}

      {deal.stats &&
        (deal.stats.calls != null ||
          deal.stats.emails != null ||
          deal.stats.first_reply_label) && (
          <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 border-t border-slate-900/[0.06] pt-3 text-[11px] text-slate-500">
            {deal.stats.calls != null && (
              <span>
                {deal.stats.calls}{" "}
                {deal.stats.calls === 1 ? "call" : "calls"}
              </span>
            )}
            {deal.stats.emails != null && (
              <>
                <span aria-hidden>·</span>
                <span>
                  {deal.stats.emails}{" "}
                  {deal.stats.emails === 1 ? "email" : "emails"}
                </span>
              </>
            )}
            {deal.stats.first_reply_label && (
              <>
                <span aria-hidden>·</span>
                <span>{deal.stats.first_reply_label}</span>
              </>
            )}
          </div>
        )}
    </Link>
  );
}

function Timeline({ steps }: { steps?: DealJourneyStep[] }) {
  // Defensive against a backend that omits the journey for a deal —
  // we used to crash on `steps.length` / `steps.map`.
  if (!steps || steps.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <ol className="flex min-w-full items-start gap-1">
        {steps.map((step, i) => (
          <TimelineNode
            key={`${step.key ?? "step"}-${step.occurred_at ?? ""}-${i}`}
            step={step}
            index={i}
            isLast={i === steps.length - 1}
          />
        ))}
      </ol>
    </div>
  );
}

function TimelineNode({
  step,
  index,
  isLast,
}: {
  step: DealJourneyStep;
  index: number;
  isLast: boolean;
}) {
  const palette = paletteFor(step.color);
  const dateLabel = safeFormat(step.occurred_at);
  // Sequential reveal — each dot lands ~100 ms after the previous so
  // the timeline reads as a journey unfolding left-to-right.
  const delay = index * 0.1;
  return (
    <li className="flex min-w-0 flex-1 items-start gap-1">
      <div className="flex min-w-0 flex-col items-center text-center">
        <motion.span
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25, delay, ease: "backOut" }}
          className={cn(
            "flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-white",
            palette.fillBgClass,
          )}
          aria-hidden
        />
        <div className="mt-1 max-w-[110px]">
          <div className="text-[11px] font-medium text-slate-700">
            {step.label}
          </div>
          <div className="text-[10px] text-slate-400">{dateLabel}</div>
          <div className="mt-0.5 flex items-center justify-center gap-1 text-[10px] text-slate-500">
            <ActorGlyph step={step} />
            <span className="truncate">{step.actor_name}</span>
          </div>
        </div>
      </div>
      {!isLast && (
        <motion.div
          initial={{ scaleX: 0, originX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.25, delay: delay + 0.05 }}
          className="mt-3 h-0.5 flex-1 origin-left rounded-full bg-slate-200"
          aria-hidden
        />
      )}
    </li>
  );
}

function ActorGlyph({ step }: { step: DealJourneyStep }) {
  if (step.actor_kind === "engine") {
    return <Cog className="h-3 w-3 text-slate-400" />;
  }
  if (step.actor_kind === "prospect") {
    return <MessageSquare className="h-3 w-3 text-slate-400" />;
  }
  // user
  const avatar = resolveAvatarColor(step.avatar_color, step.actor_id ?? "");
  return (
    <Avatar className="h-3.5 w-3.5 border border-slate-200">
      <AvatarFallback
        className={cn("text-[7px] font-semibold", avatar.bg, avatar.text)}
      >
        {initials(step.actor_name)}
      </AvatarFallback>
    </Avatar>
  );
}

function ContributorRow({ contributor }: { contributor: DealContributor }) {
  const avatar = resolveAvatarColor(
    contributor.avatar_color,
    contributor.member_id,
  );
  const role = contributor.role;
  const roleTone = roleToneFor(role);
  return (
    <div className="flex items-center gap-2 text-xs">
      <Avatar className="h-5 w-5 border border-slate-200">
        <AvatarFallback
          className={cn("text-[9px] font-semibold", avatar.bg, avatar.text)}
        >
          {initials(contributor.member_name)}
        </AvatarFallback>
      </Avatar>
      <span className="font-medium text-slate-800">
        {contributor.member_name}
      </span>
      <span
        className={cn(
          "rounded-full px-1.5 py-0 text-[10px] font-semibold",
          roleTone,
        )}
      >
        {role}
      </span>
      <span className="truncate text-slate-500">{contributor.summary}</span>
    </div>
  );
}

function roleToneFor(role: string): string {
  const lower = role.toLowerCase();
  if (lower.includes("clos")) return "bg-emerald-100 text-emerald-700";
  if (lower.includes("qual")) return "bg-amber-100 text-amber-700";
  return "bg-slate-100 text-slate-600";
}
